import { useCallback, useMemo } from 'react'

import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import { getCampaignAssistantDisplayName } from '~/features/campaigns/utils'
import {
  getCampaignJunctions,
  isSharedCampaign,
} from '~/features/campaigns/utils/campaignJunctions'

type TAssistantOption = {
  id: string
  label: string
}

export default function useCampaignAssistantOptions(
  campaign: Record<string, unknown> | null,
) {
  const { agents, selectedAgent } = useGetAgentCentralized()
  const selectedAgentId = String(selectedAgent?.[EAgentKeys.id] || '')
  const assistantNameById = useMemo(
    () =>
      new Map(
        (agents ?? []).map((agent) => [
          String(agent[EAgentKeys.id] || ''),
          String(
            agent[EAgentKeys.agentReferenceName] ||
              agent[EAgentKeys.agentName] ||
              agent[EAgentKeys.businessName] ||
              '',
          ).trim(),
        ]),
      ),
    [agents],
  )

  const assistantOptions = useMemo(
    () =>
      getCampaignJunctions(campaign)
        .map((junction) => {
          const id = String(junction.agentId || junction.Agent?.id || '')
          if (!id) return null

          const fallbackLabel = assistantNameById.get(id) || id
          const displayName = getCampaignAssistantDisplayName(junction)
          return {
            id,
            label:
              displayName && displayName !== id ? displayName : fallbackLabel,
          }
        })
        .filter((option): option is TAssistantOption => Boolean(option)),
    [assistantNameById, campaign],
  )

  const isShared = isSharedCampaign(campaign)
  const shouldShowAssistantSelection = isShared && assistantOptions.length > 1

  const getDefaultAssistantId = useCallback(
    (currentAssistantId: string) => {
      if (!assistantOptions.length) {
        return ''
      }

      if (assistantOptions.some((option) => option.id === currentAssistantId)) {
        return currentAssistantId
      }

      if (assistantOptions.some((option) => option.id === selectedAgentId)) {
        return selectedAgentId
      }

      return assistantOptions[0]?.id || ''
    },
    [assistantOptions, selectedAgentId],
  )

  return {
    assistantOptions,
    shouldShowAssistantSelection,
    selectedAgentId,
    getDefaultAssistantId,
  }
}
